import { CONSTANTS_MESSAGE } from 'constants/ConstantsMessages';
import { SHARED_ACTIONS } from 'constants/Actions/Shared';
import { DATES_FORMATS } from 'constants/Constants';
import { ATTACHMENT_CONSTANTS } from 'constants/Attachments';
import { DEADLINE_TYPES } from 'constants/DeadlineTypes';

var oDayJs = require('dayjs');

/**
 * Función que realiza un dispatch global
 * 
 * @param {Action} sTypeAction Tipo de accion que se envia
 * @param {Data} oData Datos que se deseean enviar al redux
 * 
 * @author Leandro Curbelo
 */
export const actionDispatch = (sTypeAction, oData = null) => ({
    type: sTypeAction,
    data: oData
});
/**
 * Función que maneja los mensajes de error tomados desde el catch de los servicios
 * 
 * @param {string|object} oMessage Mensaje que se mostrara en pantalla
 * 
 * @author Leandro Curbelo
 */
export const actionManageMessage = (oMessage) => {
    return dispatch => {
        if (typeof oMessage === 'string')
            return dispatch(actionMessage(oMessage));
        if (oMessage?.data?.message)
            return dispatch(actionMessage(oMessage.data.message));
        // no mostrar errores de permisos con mensajes genéricos
        if (oMessage.status !== 418)
            dispatch(actionMessage());
    }
}
/**
 * Función que activa el mensaje Toast
 * 
 * @param {string} sMessage Mensaje que se mostrara en pantalla
 * 
 * @author Leandro Curbelo
 */
export const actionMessage = (sMessage = CONSTANTS_MESSAGE.DEFAULT_MESSAGE_ERROR, sMessageType = CONSTANTS_MESSAGE.TYPE_ERROR) => ({
    type: SHARED_ACTIONS.MESSAGE,
    sMessage: sMessage,
    sMessageType: sMessageType
})
/**
 * Función que activa el mensaje Toast
 * 
 * @author Leandro Curbelo
 */
export const actionCleanMessage = () => {
    return dispatch => {
        dispatch(actionDispatch(SHARED_ACTIONS.CLEAN_MESSAGE));
    }
}
/**
 * Función que limpia el contador de loadings
 * 
 * @author Leandro Curbelo
 */
export const actionCleanLoading = () => {
    return dispatch => {
        dispatch(actionDispatch(SHARED_ACTIONS.CLEAN_LOADING));
    }
}
/**
 * Función que activa el loading
 * 
 * @author Leandro Curbelo
 */
export const actionActiveLoading = () => {
    return dispatch => {
        dispatch(actionDispatch(SHARED_ACTIONS.ACTIVE_LOADING, null));
    }
};
/**
 * Función que desactiva el loading
 * 
 * @author Leandro Curbelo
 */
export const actionDesactiveLoading = () => {
    return dispatch => {
        dispatch(actionDispatch(SHARED_ACTIONS.DESACTIVE_LOADING, null));
    }
};
/**
 * Función que permite desde la vista enviar un mensaje
 * 
 * @param {string} sMessage Mensaje que se mostrara en pantalla
 * 
 * @author Leandro Curbelo
 */
export const actionSendMessage = (sMessage) => {
    return dispatch => {
        dispatch(actionMessage(sMessage));
    }
}
/**
 * Función que retorna la etiqueta de traducción que se le asigna al archivo dependiendo de su estado
 * 
 * @param {boolean} bStatus Estado actual del registro
 * 
 * @author Leandro Curbelo
 */
export const GetStatusLabel = (bStatus = false) => {
    switch (bStatus) {
        case true:
        case 1:
            return 'STATUS_ACTIVE';
        case false:
        case 0:
        default:
            return 'STATUS_INACTIVE';
    }
}
/**
 * Función que permite castear un booleano o 0 1 a string para la vista
 * 
 * @param {number | boolean} nBool Booleano o entero que representa un booleano
 * 
 * @author Leandro Curbelo
 */
export const CastBool = (nBool) => {
    let sValue = 'S/D';
    switch (nBool) {
        case 1:
        case true:
            sValue = 'Sí';
            break;
        case 0:
        case false:
        default:
            sValue = 'No';
            break;
    }
    return sValue;
}
/**
 * Función que le da un formato a todas las fechas del sistema, centraliza el uso de moment y el formato de las fechas
 * Casteo de fechas del tipo Date
 * 
 * @param {string} sDate Fecha en formato Date o string
 * @param {string} sFormat Formato de salida de la fecha recibida
 * 
 * @author Leandro Curbelo
 */
export const CastDate = (sDate, sFormat = DATES_FORMATS.DEFAULT) => {
    return oDayJs(sDate).format(sFormat);
}
/**
 * Función que le da un formato a todas las fechas del sistema, centraliza el uso de moment y el formato de las fechas
 * Casteo de fechas en formato Hora.
 * 
 * @param {string} sDate Fecha en formato Date o string
 * @param {string} sFormat Formato de salida de la fecha recibida
 * 
 * @author Leandro Curbelo
 */
export const CastTime = (sDate, sFormat = DATES_FORMATS.TIME_DEFAULT) => {
    return oDayJs(sDate).format(sFormat);
}
/**
 * Función que le da un formato a todas las fechas del sistema, centraliza el uso de moment y el formato de las fechas
 * Casteo de fechas del tipo Timestamp
 * 
 * @param {string} sDate Fecha en formato Date o string
 * @param {string} sFormat Formato de salida de la fecha recibida
 * 
 * @author Leandro Curbelo
 */
export const CastDateTime = (sDate, sFormat = DATES_FORMATS.TIMESTAMPS_DEFAULT) => {
    return oDayJs(sDate).format(sFormat);
}
/**
 * Función que recibe un string que representa una fecha y retorna un objeto Date.
 * 
 * @param {string} sDate Fecha que se desea transformar en objeto
 * 
 * @returns {Date}
 */
export const GetDateObject = (sDate) => {
    return sDate ? oDayJs(sDate).toDate() : new Date();
}
/**
 * Función que retorna la diferencia entre fechas en la unidad recibida
 * 
 * @param {Date} sStartDate Fecha anterior
 * @param {Date} sFinishDate Fecha posterior
 * @param {string} sUnit Unidad en la que se desea obtener la diferencia
 * 
 * @author Leandro Curbelo
 */
export const GetTimeDifference = (sStartDate, sFinishDate = new Date(), sUnits = DATES_FORMATS.CONSTANTS.UNITS.DAYS) => {
    let dStartDate = oDayJs(sStartDate),
        dFinishDate = oDayJs(sFinishDate);
    return dFinishDate.diff(dStartDate, sUnits);
}
/**
 * Función que resta unidades a una fecha
 * 
 * @param {Date|string} dDate Fecha que se desea modificar
 * @param {string} nValue Cantidad que se desea restar a la fecha
 * @param {string} sUnits Unidad que se le desea restar a la fecha
 * 
 * @author Leandro Curbelo
 */
export const SubtractToDate = (dDate = new Date(), nValue, sUnits = DATES_FORMATS.CONSTANTS.UNITS.DAYS) => {
    return oDayJs(dDate).subtract(nValue, sUnits).toDate();
}
/**
 * Función que recibe el tamaño en bytes de un archivo y retorna un string visible para la visualización
 * 
 * @param {number} nSize Tamaño del archivo en bytes
 * 
 * @returns {string}
 * 
 * @author Leandro Curbelo
 */
export const GetFileSize = (nSize) => {
    const aUnits = ['bytes', 'KB', 'MB', 'GB', 'TB'];
    let nUnitCount = 0, nSize10 = parseInt(nSize, 10) || 0;
    while (nSize10 >= 1024 && ++nUnitCount)
        nSize10 = nSize10 / 1024;
    return (nSize10.toFixed(nSize10 < 10 && nUnitCount > 0 ? 1 : 0) + ' ' + aUnits[nUnitCount]);
}
/**
 * Función que checkea que el archivo sea válido para subir al sistema
 * 
 * @param {object} oFile Objeto que representa el archivo adjunto
 * @param {array} aFiles Arreglo de todos los archivos seleccionados en ese momento
 * 
 * @author Leandro Curbelo
 */
export const CheckFiles = (oFile, aFiles = []) => {
    if (oFile.size && oFile.size > ATTACHMENT_CONSTANTS.MAX_SIZE)
        return { status: false, class: 'file-item-header-danger', message: 'FILE_SIZE_EXCEED' };
    let aFilter = aFiles.filter((oItem) => (oItem.name === oFile.name && oItem.size === oFile.size));
    if (aFilter.length > 1)
        return { status: true, class: 'file-item-header-warning', message: 'FILE_NAME_DUPLICATED' };
    return { status: true, class: 'file-item-header', message: 'FILE_ACCEPTED' };
}
/**
 * Función que retorna el label a mostrar según el tipo de fecha límite que tenga el tipo de análisis
 *  o el tipo de paternidad.
 * 
 * @param {number} nValue Valor del tipo de fecha límite
 * 
 * @author Leandro Curbelo
 */
export const GetDeadlineTypeLabel = (nValue) => {
    let sLabel = '';
    switch (nValue) {
        case DEADLINE_TYPES.NORMAL.value:
        default:
            sLabel = DEADLINE_TYPES.NORMAL.label;
            break;
        case DEADLINE_TYPES.BUSSINESS.value:
            sLabel = DEADLINE_TYPES.BUSSINESS.label;
            break;
        case DEADLINE_TYPES.OTHER.value:
            sLabel = DEADLINE_TYPES.OTHER.label;
            break;
    }
    return sLabel;
}

export const EmailValidator = (sEmail = '') => {
    const oEmailValidator = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return oEmailValidator.test(sEmail.toLowerCase());
}

export const ConcatNames = (sFirstName, sSecondName) => {
    if (!sFirstName && !sSecondName)
        return '';
    if (sFirstName && sSecondName)
        return `${sFirstName} ${sSecondName}`;
    return sFirstName ? sFirstName : sSecondName;
}

export const LimitText = (sText, nLimit) => {
    if (sText.length <= nLimit) {
        return sText;
    }
    let trimmedString = sText.substr(0, nLimit);
    trimmedString = trimmedString.substr(0, Math.min(trimmedString.length, trimmedString.lastIndexOf(" ")))
    return trimmedString + "...";
}